import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { FiSend, FiPaperclip, FiSmile, FiPlus, FiLock, FiUnlock, FiVideo, FiMusic, FiChevronLeft, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import AccentIcons from '../../icons/Icons';
import './chat.css';
import { ChatMessage, mapChatMessages, makeReplyPreview } from './ChatMessage';
import ServiceWindowBanner from './ServiceWindowBanner';
import { toastError, format, A, t } from '../../services/HelperService';
import * as MessageService from '../../services/MessageService'
import { RTLQueryFetcher } from '../RTLFetch';
import { getUser } from '../../services/UserService';
import { AccentSpinner } from '../AccentSpinner';
import { isMobile } from 'react-device-detect';
import { looksLikeHtml, renderSafeHtml } from './ChatHtml';
import { update, queryNoTracking } from '../../services/DataService';
import { AccentPopup } from '../../../components/dialogs/AccentPopup';
import StatusPill from "./StatusPill";
import AssigneeBadge from './AssigneeBadge';
import AssigneePickerPopover from "./AssigneePickerPopover";
import { OpenNewLeadForChat } from "./helpers/OpenNewLeadForChat";
import { OpenNewContactForChat } from "./helpers/OpenNewContactForChat";
import { OpenLinkJobsDlg } from "./helpers/OpenLinkJobsDlg";

const MAX_ATTACHMENT_SIZE = 100 * 1024 * 1024;

const classifyForPayload = (att) => {
    const mime = (att.type || '').toLowerCase();

    if (att.isSticker === true || mime === 'image/webp') {
        return { ui: 'image', payload: 'Sticker' };
    }

    if (mime.startsWith('image/')) return { ui: 'image', payload: 'Image' };
    if (mime.startsWith('video/')) return { ui: 'video', payload: 'Video' };
    if (mime.startsWith('audio/')) return { ui: 'audio', payload: 'Audio' };

    return { ui: 'attachment', payload: 'Document' };
};

export const ChatCtrl = forwardRef(({
    ctx,
    referenceNo,
    chatID,
    subject,
    status,
    representativeAssigneeName,
    teamAssigneeName,
    contactPoint,
    contactPointDisplayName,
    contactID,
    contactName,
    fromDisplayName,
    openDate,
    closeDate,
    archivedDate,
    archivedBy,
    linkedJobs = [],
    isReadOnly = false,
    query,
    serviceWindowQuery,
    templateQuery,
    generateTemplateContent,
    saveMessage,
    alternateChannelSaveMessage = [],
    markAsRead,
    markAsResent,
    onSubjectUpdated,
    onStatusUpdated,
    getPossibleContactMatch,
    onContactLinked,
    onAssigneeUpdated,
    onBackClick,
    showBackButton = false,
    onLinkedJobsUpdated
}, ref) => {
    const inputAreaRef = useRef(null);
    const bottomLockRef = useRef(true);
    const currentUserName = getUser().FullName;

    const isArchived = archivedDate && archivedBy;
    const readOnly = isReadOnly || false; 

    const hasCtxJob = !!(ctx?.JobID);
    const [selectedJobIdForTemplate, setSelectedJobIdForTemplate] = useState(null);
    const [templatePhase, setTemplatePhase] = useState('templates'); // 'job' | 'templates'

    const [isInternal, setIsInternal] = useState(false);

    const [subjectValue, setSubjectValue] = useState(subject || "");
    const [isEditingSubject, setIsEditingSubject] = useState(false);
    const subjectInputRef = useRef(null);
    const [statusValue, setStatusValue] = useState(status || "");
    const [closeDateValue, setCloseDateValue] = useState(closeDate ?? null);

    useEffect(() => { setStatusValue(status || ""); }, [status]);
    useEffect(() => { setSubjectValue(subject || ""); }, [subject]);
    useEffect(() => { setCloseDateValue(closeDate ?? null); }, [closeDate]);

    const [messages, setMessages] = useState([]);
    const [serviceWindow, setServiceWindow] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateID, setSelectedTemplateID] = useState(0);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [dataState, setDataState] = useState({ skip: 0, take: 50, sort: [{ field: 'Time', dir: 'desc' }] });
    const [loading, setLoading] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);
    const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
    const [isWindowOpen, setIsWindowOpen] = useState(false);
    const [totalMessages, setTotalMessages] = useState(0);
    const [replyingTo, setReplyingTo] = useState(null);
    const [suggestedMatches, setSuggestedMatches] = useState([]);
    const [suggestionsReady, setSuggestionsReady] = useState(false);
    const [chipsPhase, setChipsPhase] = useState('idle'); // 'idle' | 'enter' | 'exit'
    const [detailsOpen, setDetailsOpen] = useState(!isMobile);
    const [linkedJobsLocal, setLinkedJobsLocal] = useState(linkedJobs);
    const prevMatchCountRef = useRef(0);

    // --- Mentions state ---
    const mentionApiRef = useRef(queryNoTracking('Chat_GetMentionableUsers'));
    const [mentionsOpen, setMentionsOpen] = useState(false);
    const [mentionOptions, setMentionOptions] = useState([]);
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionIndex, setMentionIndex] = useState(0);
    const [selectedMentions, setSelectedMentions] = useState(() => new Map());
    const mentionNavModeRef = useRef('mouse');
    const mentionNavTimerRef = useRef(null);
    const mentionListRef = useRef(null);
    const mentionStartRef = useRef(null);
    const mentionWrapRef = useRef(null);
    const [mentionPopupLeft, setMentionPopupLeft] = useState(0);

    // --- Assignee picker state ---
    const [assigneeOpen, setAssigneeOpen] = useState(false);
    const [committedAssignee, setCommittedAssignee] = useState(null);

    const statusAnchorRef = useRef(null);
    const [uniqPopup, setUniqPopup] = useState(false);

    // when the list first appears, play the enter animation
    useEffect(() => {
        const count = suggestedMatches.length;
        const prev = prevMatchCountRef.current;
        if (prev === 0 && count > 0) setChipsPhase('enter');
        prevMatchCountRef.current = count;
    }, [suggestedMatches.length]);

    // helper to animate-out then clear
    const hideChipsWithAnimation = useCallback(() => {
        setChipsPhase('exit');
        setTimeout(() => {
            setSuggestedMatches([]);
            setChipsPhase('idle');
        }, 300);
    }, []);

    useEffect(() => {
         const inputEl = inputAreaRef.current;
         const msgsEl = messagesAreaRef.current;
         if (!inputEl || !msgsEl) return;
        
        const BASE = 65;
        const MAX = 120;
        const EASE = 0.35;
        
        let disposed = false;
        const apply = () => {
            if (disposed) return;
            const i = inputAreaRef.current;
            const m = messagesAreaRef.current;
            if (!i || !m) return;
            
            // measure outer box height
            let h = i.getBoundingClientRect().height || 0;
            // subtract vertical margins so they don't bloat the offset
            const cs = getComputedStyle(i);
            const vm = (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
            h = Math.max(0, h - vm);
            
            // ease + clamp
            const extra = Math.max(0, h - BASE);
            const eased = BASE + Math.round(extra * EASE);
            const offset = Math.min(MAX, Math.max(BASE, eased));
            
            m.style.setProperty('--chat-bottom-offset', `${offset}px`);
            };
        
        apply();
        const ro = new ResizeObserver(apply);
        ro.observe(inputEl);
        window.addEventListener('resize', apply);
        return () => {
            disposed = true;
            try { ro.unobserve(inputEl); } catch { }
            ro.disconnect();
            window.removeEventListener('resize', apply);
        };
        }, []);

    useEffect(() => setLinkedJobsLocal(linkedJobs), [linkedJobs]);

    const queryFetcherRef = useRef(null);
    const templateQueryFetcherRef = useRef(null);

    const [inputValue, setInputValue] = useState("");
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [unreadMessageIds, setUnreadMessageIds] = useState(() => new Set());
    const [unreadMentionMessageIds, setUnreadMentionMessageIds] = useState(() => new Set());
    const [isUserMessageJustSent, setIsUserMessageJustSent] = useState(false);
    const [firstUnreadIndex, setFirstUnreadIndex] = useState(null);
    const messageEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);
    const messagesAreaRef = useRef(null);
    const observerRef = useRef(null);
    const skipScrollRef = useRef(false);

    const [templateMode, setTemplateMode] = useState('none');
    const [templateState, setTemplateState] = useState('closed');
    const isTemplatesOpen = templateState === 'open' || templateState === 'opening';
    const isTemplateLocked = selectedTemplateID !== 0 && templateMode === 'locked';

    const isHtml = looksLikeHtml(inputValue || '');
    const allowTemplateOutsideWindow = !isInternal && (selectedTemplateID !== 0) && (templateMode === 'locked');
    const isSendDisabled = (
        isGeneratingTemplate ||
        isArchived ||
        readOnly ||
        (!isInternal && !isWindowOpen && !allowTemplateOutsideWindow)
    );
    const canSendNow = (Boolean(inputValue.trim()) || pendingAttachments.length > 0) && !isSendDisabled;
    // show preview when disabled OR explicitly locked to a template
    const showHtmlPreview = (isSendDisabled || isTemplateLocked) && (selectedTemplateID !== 0 || isHtml);

    const handleLinkContact = async (match) => {
        try {
            if (!chatID) throw new Error('Missing chatID');
            const contactId = Number(match?.ContactID ?? match?.ID);
            if (!contactId) throw new Error('Missing contactId');

            const ok = await update('Chat_LinkContact', { ChatId: chatID, ContactId: contactId });
            if (!ok) throw new Error('Server returned false');

            // Update LHS immediately
            const contactName = [match?.FirstName, match?.LastName].filter(Boolean).join(' ') || null;
            onContactLinked?.(contactId, contactName);

            // Hide the chips with animation
            hideChipsWithAnimation();

            // Refresh this view so bubbles show the new sender name
            await fetchMessages();
            await refreshServiceWindow();
        } catch (err) {
            console.error('Link contact failed', err);
            toastError(t("application_strings.views.chat.rightPane.chat.errors.linkContactFailed"));
        }
    };

    const beginEditSubject = () => {
        setIsEditingSubject(true);
        setTimeout(() => subjectInputRef.current?.focus(), 0);
    };

    const cancelEditSubject = () => {
        setSubjectValue(subject || "");
        setIsEditingSubject(false);
    };

    const commitSubject = async () => {
        const next = (subjectValue || "").trim();
        const prev = subject || "";
        setSubjectValue(next);
        setIsEditingSubject(false);
        try {
            if (!chatID) throw new Error('Missing chatID');
            const ok = await update('Chat_UpdateSubject', { ChatId: chatID, Subject: next });
            if (!ok) throw new Error('Server returned false');
            onSubjectUpdated?.(next);
        } catch (err) {
            console.error('Update subject failed', err);
            setSubjectValue(prev);
            toastError(t("application_strings.views.chat.rightPane.chat.errors.updateSubjectFailed"));
        }
    };

    const handleChangeStatus = async (next) => {
        const prev = statusValue;
        const prevClosedAt = closeDateValue;
        setStatusValue(next);
        if (next === 'closed' && !closeDateValue) {
            setCloseDateValue(new Date().toISOString());
        } else if (prev === 'closed' && next !== 'closed') {
            setCloseDateValue(null);
        }
        try {
            if (!chatID) throw new Error("Missing chatID");
            const ok = await update("Chat_UpdateStatus", { ChatId: chatID, Status: next });
            if (!ok) {
                setUniqPopup(true);
                throw new Error("Server returned false");
            }
            onStatusUpdated?.(next); 
        } catch (err) {
            console.error("Update status failed", err);
            setStatusValue(prev);
            setCloseDateValue(prevClosedAt);
            toastError("Could not update status. Please try again.");
        }
    };

    // Initialize or update the query fetcher when the query prop changes
    const prevQueryChatIdRef = useRef(null);

    useEffect(() => {
        if (!query) { queryFetcherRef.current = null; return; }

        // Only recreate the fetcher when the *chat* changes
        if (prevQueryChatIdRef.current === chatID) {
            return;
        }

        const querySource = (typeof query === 'function') ? query() : query;
        queryFetcherRef.current = new RTLQueryFetcher({ query: querySource });

        setDataState({ skip: 0, take: 50, sort: [{ field: 'Time', dir: 'desc' }] });
        setIsInitialLoad(true);
        setMessages([]);
        setTotalMessages(0);
        setFirstUnreadIndex(null);

        prevQueryChatIdRef.current = chatID;
    }, [query, chatID]);


    useEffect(() => {
        if (templateQuery) {
            const templateQuerySource = typeof templateQuery === 'function' ? templateQuery() : templateQuery;
            templateQueryFetcherRef.current = new RTLQueryFetcher({ query: templateQuerySource });
        } else {
            templateQueryFetcherRef.current = null;
        }
    }, [templateQuery]);

    useEffect(() => {
        setInputValue('');
        setSelectedTemplateID(0);
        setTemplateMode('none');
        setTemplateState('closed');
        setSelectedJobIdForTemplate(null);
        setReplyingTo(null);
        setPendingAttachments([]);
        setSelectedMentions(new Map());
        setTemplatePhase('templates');
    }, [chatID]);

    const fetchMessages = useCallback(async () => {
        if (!queryFetcherRef.current) {
            console.warn("ChatCtrl: Query fetcher not available.");
            return;
        }
        if (isInitialLoad) {
            setLoading(true);
        }
        if (dataState.skip > 0) {
            setIsLoadingOlder(true);
            skipScrollRef.current = true;
        }
        try {
            const result = await queryFetcherRef.current.fetchData(dataState);
            const mappedMessages = mapChatMessages(result?.data || result || []);
            const reversedMappedMessages = mappedMessages.reverse();

            const nextMessages = dataState.skip > 0
                ? [...reversedMappedMessages, ...messages]
                : reversedMappedMessages;

            setMessages(nextMessages);
            setTotalMessages(nextMessages.length);
        } catch (error) {
            console.error("Error fetching chat messages:", error);
            setMessages([]);
            setTotalMessages(0);
        } finally {
            setLoading(false);
            if (isInitialLoad && dataState.skip === 0) {
                setIsInitialLoad(false);
            }
            setIsLoadingOlder(false);
        }
    }, [dataState, isInitialLoad]);

    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    const refreshServiceWindow = useCallback(async () => {
        if (!serviceWindowQuery) return;

        try {
            const result = await (typeof serviceWindowQuery === 'function'
                ? serviceWindowQuery()
                : serviceWindowQuery);

            setServiceWindow(result);
            setIsWindowOpen(!!result?.IsOpen);
        } catch (err) {
            console.error('Failed to load customer service window', err);
            setIsWindowOpen(false);
        }
    }, [serviceWindowQuery])

    useEffect(() => {
        refreshServiceWindow();
    }, [refreshServiceWindow]);

    const fetchTemplates = useCallback(async () => {
        if (!templateQueryFetcherRef.current) {
            console.warn("ChatCtrl: Template Query fetcher not available.");
            return;
        }

        if (isInitialLoad) {
            setLoading(true);
        }

        try {
            const result = await templateQueryFetcherRef.current.fetchData(null);

            // Handle both array and object with data property
            const dataArray = Array.isArray(result) ? result : (result?.data || []);

            // Filter out templates with URL buttons
            const filtered = dataArray.filter(t => {
                if (!t.Body) return true;
                return !/<button\s+href=/i.test(t.Body);
            });

            setTemplates(filtered);
        } catch (error) {
            console.error("Error fetching templates:", error);
            setTemplates([]);
        } finally {
            setLoading(false);
            if (isInitialLoad) {
                setIsInitialLoad(false);
            }
            setIsLoadingOlder(false);
        }
    }, [isInitialLoad]);

    const prevHasJobsRef = useRef(false);

    useEffect(() => {
        const hasJobsNow =
            !!ctx?.JobID || (Array.isArray(linkedJobsLocal) && linkedJobsLocal.length > 0);

        if (hasJobsNow && !prevHasJobsRef.current) {
            // Recreate the fetcher in case templateQuery() closes over prior state
            if (typeof templateQuery === 'function') {
                const templateQuerySource = templateQuery();
                templateQueryFetcherRef.current = new RTLQueryFetcher({ query: templateQuerySource });
            }
            fetchTemplates();
        }
        prevHasJobsRef.current = hasJobsNow;
    }, [ctx?.JobID, linkedJobsLocal?.length, templateQuery, fetchTemplates]);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    useEffect(() => {
        setUnreadMessageIds(prevSet => {
            const newSet = new Set(prevSet);
            let changed = false;
            messages.forEach(message => {
                if (!message.isOutbound && message.status !== 'read') {
                    const messageIdStr = String(message.id);
                    if (!newSet.has(messageIdStr)) {
                        newSet.add(messageIdStr);
                        changed = true;
                    }
                }
            });
            return changed ? newSet : prevSet;
        });

        setUnreadMentionMessageIds(() => {
            const next = new Set();
            messages.forEach(m => {
                if (m.isOutbound && m.isMentionUnread) {
                    next.add(String(m.id));
                }
            });
            return next;
        });
    }, [messages]);

    // --- Intersection Observer ---
    useEffect(() => {
        if (!markAsRead) return;

        const handleIntersection = (entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const messageId = String(target.dataset.messageId);

                    if (messageId && unreadMessageIds.has(messageId) && markAsRead) {
                        const message = messages.find(m => String(m.id) === messageId);
                        if (message) {
                            markAsRead(messageId);
                        }
                    }

                    if (messageId && unreadMentionMessageIds.has(messageId)) {
                        update('Chat_Mention_MarkAsRead', { ChatId: chatID, ChatMessageId: Number(messageId) });
                        // prevent duplicate calls
                        setUnreadMentionMessageIds(prev => {
                            const next = new Set(prev); next.delete(messageId); return next;
                        });
                    }
                }
            });
        };

        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver(handleIntersection, {
            root: messagesAreaRef.current,
            rootMargin: '0px',
            threshold: 0.5
        });

        const observer = observerRef.current;
        const messageElements = messagesAreaRef.current?.querySelectorAll('.chat-message-row[data-message-id]');

        if (messageElements) {
            messageElements.forEach(el => {
                const messageId = String(el.dataset.messageId);
                if (unreadMessageIds.has(messageId) || unreadMentionMessageIds.has(messageId)) {
                    observer.observe(el);
                }
            });
        }

        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
    }, [messages, markAsRead, unreadMessageIds, unreadMentionMessageIds]);

    useEffect(() => {
        let cancelled = false;
        setSuggestionsReady(false);
        setSuggestedMatches([]);
        // Only suggest if this chat isn't already linked to a Contact
        if (!contactID && contactPoint && typeof getPossibleContactMatch === "function") {
            (async () => {
                try {
                    const result = await getPossibleContactMatch(contactPoint);
                    if (cancelled) return;
                    
                    const arr = Array.isArray(result) ? result : (result ? [result] : []);
                    if (arr.length) setSuggestedMatches(arr);
                } finally {
                    if (!cancelled) setSuggestionsReady(true);
                }

            })();
        } else {
            setSuggestionsReady(true);
        }
        return () => { cancelled = true; };
    }, [chatID, contactID, contactPoint, getPossibleContactMatch]);

    useEffect(() => {
        if (!chatID) return;

        const handleChatUpdate = (e) => {
            if (e.ConversationID === chatID) {
                fetchMessages();
                refreshServiceWindow();
            }
        };

        const listenerId = `ChatCtrl_${chatID}`;
        MessageService.Helper.register(listenerId, MessageService.MessageTypes.ChatUpdate, handleChatUpdate);

        return () => {
            MessageService.Helper.unregister(listenerId, MessageService.MessageTypes.ChatUpdate);
        };
    }, [chatID, fetchMessages, refreshServiceWindow]);

    useEffect(() => {
        const index = messages.findIndex(m => unreadMessageIds.has(String(m.id)));
        if (index !== -1) {
            setFirstUnreadIndex(index);
        } else {
            setFirstUnreadIndex(null);
        }
    }, [messages, firstUnreadIndex, unreadMessageIds]);

    const extractMentionGuids = (text, mentionOptions) => {
        const matches = Array.from(
            (text || '').matchAll(/(^|[\s])@([A-Za-z0-9][\w.'-]*(?:\s+[A-Za-z0-9][\w.'-]*)?)(?=$|[\s,.!?])/g)
        ).map(m => (m[2] || '').trim().toLowerCase());

        if (matches.length === 0) return [];
        const wanted = new Set(matches);
        return mentionOptions
            .filter(o => wanted.has(String(o.RepresentativeName || '').toLowerCase()))
            .map(o => o.RepresentativeId)
            .filter(Boolean);
    };

    const buildMentionIds = () => {
        const explicit = Array.from(selectedMentions.keys());
        if (explicit.length) return explicit; 
        return extractMentionGuids(inputValue, mentionOptions);
    };

    const resetCompose = () => {
        setSelectedTemplateID(0);
        setTemplateMode('none');
        setTemplateState('closed');
        setReplyingTo(null);
        setSelectedJobIdForTemplate(null);
    }

    const handleSendMessage = async () => {
        if (!inputValue.trim() && pendingAttachments.length === 0) return;

        if (isSendDisabled) {
            toastError(t("application_strings.views.chat.rightPane.chat.errors.cannotSendOutsideWindow"));
            return;
        }

        if (!inputValue.trim() && pendingAttachments.length === 0) return;

        const replyToIdForPayload = replyingTo && /^\d+$/.test(String(replyingTo.id))
            ? Number(replyingTo.id) : undefined;

        if (inputValue.trim()) {
            const isTemplate = selectedTemplateID !== 0 && templateMode === 'locked';
            const isHtml = looksLikeHtml(inputValue);

            const uiMessage = {
                id: `local-${Date.now()}`,
                templateID: selectedTemplateID,
                type: 'text',
                text: inputValue,
                isOutbound: true,
                isInternal: isInternal,
                senderName: currentUserName,
                time: new Date().toISOString(),
                status: isInternal ? 'sent' : 'queued',
                contentType: isTemplate ? 'Template' : 'Text',
                isHtml: isHtml || isTemplate,
                replyTo: replyingTo || null
            };

            setMessages(prev => [...prev, uiMessage]);
            setInputValue("");
            setIsUserMessageJustSent(true);

            if (saveMessage) {
                const mentionedRepresentativeIds = buildMentionIds();
                const jobIdForSend =
                    isTemplate ? (selectedJobIdForTemplate ?? ctx?.JobID ?? null) : null;

                const payload = {
                    ChatID: chatID,
                    TemplateID: selectedTemplateID,
                    CustomMessage: uiMessage.text,
                    Type: selectedTemplateID === 0 ? "Text" : "Template",
                    IsInternal: isInternal,
                    MentionedRepresentativeIds: mentionedRepresentativeIds,
                    ...(jobIdForSend ? { JobID: jobIdForSend } : {}),
                };
                if (selectedTemplateID === 0 && replyToIdForPayload) {
                    payload.ReplyToID = replyToIdForPayload;
                }
                saveMessage(payload);
                setSelectedMentions(new Map());
            }

            resetCompose();
        }

        if (pendingAttachments.length && saveMessage) {
            for (const att of pendingAttachments) {
                const kind = classifyForPayload(att);
                const localUrl = att.previewUrl || URL.createObjectURL(att.file);

                const uiMsg = {
                    id: `local-${Date.now()}-${att.id}`,
                    type: kind.ui.toLowerCase(),
                    imageUrl: kind.ui === 'image' ? localUrl : null,
                    videoUrl: kind.ui === 'video' ? localUrl : null,
                    audioUrl: kind.ui === 'audio' ? localUrl : null,
                    url: localUrl,
                    documentData: null,
                    fileName: att.name,
                    mimeType: att.type,
                    isOutbound: true,
                    isInternal: isInternal,
                    senderName: currentUserName,
                    time: new Date().toISOString(),
                    status: isInternal ? 'sent' : 'queued',
                    replyTo: replyingTo || null 
                };
                setMessages(prev => [...prev, uiMsg]);
                setIsUserMessageJustSent(true);

                const payload = {
                    ChatID: chatID,
                    Type: kind.payload,
                    Attachment: att,
                    CustomMessage: null,
                    IsInternal: isInternal,
                    ...(replyToIdForPayload ? { ReplyToID: replyToIdForPayload } : {})
                };
                saveMessage(payload);

                resetCompose();
            }
            setPendingAttachments([]);
        }
    };


    const handleResendMessage = (messageId) => {
        // Find the root that contains this message (root or any of its children)
        const root = messages.find(r =>
            String(r.id) === String(messageId) ||
            (r.children?.some(c => String(c.id) === String(messageId)))
        );

        // Prefer the persisted root id for the backend; fall back to the clicked id
        const backendParentId = root?.id ?? messageId;

        // 1) Fire the API call OUTSIDE setState so it’s not timing-dependent
        if (saveMessage && /^\d+$/.test(String(backendParentId))) {
            saveMessage({ ParentID: Number(backendParentId) });
        }

        // 2) Update the UI immediately
        setMessages(prev => prev.map(r => {
            if (!root || String(r.id) !== String(root.id)) return r;

            const source =
                String(r.id) === String(messageId)
                    ? r
                    : (r.children?.find(c => String(c.id) === String(messageId)) ?? r);

            const uiChild = {
                id: `local-${Date.now()}`,
                parentId: r.id,               // always attach to the root in UI
                senderName: source.senderName,
                isOutbound: true,
                time: new Date().toISOString(),
                status: 'queued',
                channel: source.channel,
                contentType: source.contentType,
                text: source.text,
                imageUrl: source.imageUrl,
                type: source.type,
                isLocal: true,
                isHtml: source.isHtml
            };

            return { ...r, children: [...(r.children || []), uiChild] };
        }));
    };


    const handleAlternateChannelSend = async (body, channel, messageId) => {
        if (!body?.trim()) return;

        const sender = alternateChannelSaveMessage.find(s => s.channel === channel);
        if (!sender?.method) return;

        try {
            var success = await sender.method(body);
            if (markAsResent && success) markAsResent(messageId);
        } catch (err) {
            console.error("Alternate channel send failed:", err);
        }
    };

    const startReply = useCallback((msg) => {
        if (selectedTemplateID !== 0) return;
        setReplyingTo(makeReplyPreview(msg));
        setShowEmojiPicker(false);
    }, [selectedTemplateID]);

    const cancelReply = useCallback(() => setReplyingTo(null), []);

    const jumpToMessage = useCallback((messageId) => {
        if (!messageId) return;
        const row = messagesAreaRef.current?.querySelector(`.chat-message-row[data-message-id="${messageId}"]`);
        if (row) {
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            row.classList.add('chat-reply-target-highlight');
            setTimeout(() => row.classList.remove('chat-reply-target-highlight'), 1400);
        }
    }, []);

    useImperativeHandle(ref, () => ({
        jumpToMessage: (id) => jumpToMessage(id)
    }));

    const toggleEmojiPicker = () => setShowEmojiPicker(!showEmojiPicker);
    const handleEmojiClick = (emoji) => setInputValue(inputValue + emoji);

    const handleTemplateClick = async (template) => {
        setIsGeneratingTemplate(true);
        setInputValue('');
        setSelectedTemplateID(template.ID);
        setTemplateMode('locked');
        setReplyingTo(null);

        if (generateTemplateContent) {
            try {
                setLoading(true);
                const content = await generateTemplateContent(
                    template.ID,
                    { jobId: selectedJobIdForTemplate ?? ctx?.JobID ?? null }
                );
                setLoading(false);
                setInputValue(content || '');
            } catch (error) {
                console.error("Error generating template content:", error);
                setInputValue(template.Body || '');
            }
        } else {
            setInputValue(template.Body || '');
        }

        setIsGeneratingTemplate(false);
        setTemplateState('closing');
    }

    const addFiles = useCallback((fileList) => {
        const files = Array.from(fileList);
        const accepted = [];
        const rejected = [];

        for (const file of files) {
            if (Number(file.size || 0) > MAX_ATTACHMENT_SIZE) {
                rejected.push(file.name);
                continue;
            }

            accepted.push({
                id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                file,
                name: file.name,
                size: file.size,
                type: file.type,
                previewUrl: (file.type?.startsWith('image/')
                            || file.type?.startsWith('video/')
                            || file.type?.startsWith('audio/'))
                            ? URL.createObjectURL(file)
                            : null
            })
        }

        if (rejected.length) {
            toastError(t("application_strings.views.chat.rightPane.chat.attachments.tooLarge", { limitMB: '100', fileList: rejected.join(', ') }));
        }

        setPendingAttachments(prev => [...prev, ...accepted]);
    }, []);

    const removeAttachment = useCallback((id) => {
        setPendingAttachments(prev => {
            const gone = prev.find(a => a.id === id);
            if (gone?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(gone.previewUrl);
            return prev.filter(a => a.id !== id);
        });
    }, []);

    const clearAttachments = useCallback(() => {
        setPendingAttachments(prev => {
            prev.forEach(a => { if (a.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(a.previewUrl); });
            return [];
            });
    }, []);

    // --- Mention highlighting (ghost overlay) ---
    const highlightsRef = useRef(null);

    // 1) Prefetch the mentionable users once
    useEffect(() => {
        (async () => {
            try {
                const rows = await mentionApiRef.current.getAllWithCache();
                setMentionOptions(Array.isArray(rows) ? rows : (rows?.data ?? []));
            } catch { }
        })();
    }, []);

    // 2) Fast lookup of allowed names (case-insensitive)
    const mentionNameSet = React.useMemo(
        () => new Set(mentionOptions.map(o => String(o.RepresentativeName || '').toLowerCase())),
        [mentionOptions]
    );

    // 3) Only wrap tokens that match a known user
    const highlightHtml = React.useMemo(() => {
        const esc = (s) => (s || '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
        const raw = inputValue || '';

        let safe =
            mentionsOpen && mentionStartRef.current != null
                ? esc(raw.slice(0, mentionStartRef.current)) + '<span id="mention-anchor-probe"></span>' + esc(raw.slice(mentionStartRef.current))
                : esc(raw);

        return safe.replace(
            /(^|[\s])@([A-Za-z0-9][\w.'-]*(?:\s+[A-Za-z0-9][\w.'-]*)?)(?=$|[\s,.!?])/g,
            (_, pre, name) => {
                const n = name.trim().toLowerCase();
                if (!mentionNameSet.has(n)) return `${pre}@${name}`;          // ← not a real user → no highlight
                return `${pre}<span class="mention-token">@${name}</span>`;
            }
        );
    }, [inputValue, mentionsOpen, mentionNameSet]);


    const positionMentionPopup = useCallback(() => {
        if (!mentionsOpen) return;
        const hl = highlightsRef.current;
        const wrap = mentionWrapRef.current;
        const list = mentionListRef.current;
        if (!hl || !wrap) return;

        const probe = hl.querySelector('#mention-anchor-probe');
        if (!probe) return;

        const wrapRect = wrap.getBoundingClientRect();
        const probeRect = probe.getBoundingClientRect();

        let left = probeRect.left - wrapRect.left;
        if (list) {
            const max = Math.max(0, wrap.clientWidth - list.offsetWidth);
            left = Math.max(0, Math.min(left, max));
        }
        setMentionPopupLeft(left);
    }, [mentionsOpen]);

    useEffect(() => {
        const ta = textareaRef.current, hl = highlightsRef.current;
        if (!ta || !hl) return;
        const sync = () => {
            hl.scrollTop = ta.scrollTop;
            hl.scrollLeft = ta.scrollLeft;
            positionMentionPopup();
        };
        ta.addEventListener('scroll', sync, { passive: true });
        return () => ta.removeEventListener('scroll', sync);
    }, [positionMentionPopup]);

    const openMentions = useCallback(async () => {
        try {
            const rows = await mentionApiRef.current.getAllWithCache();
            setMentionOptions(Array.isArray(rows) ? rows : (rows?.data ?? []));
            setMentionQuery('');
            setMentionIndex(0);
            setMentionsOpen(true);
        } catch (e) {
            console.error('GetMentionableUsers failed', e);
            setMentionsOpen(false);
        }
        }, []);
    
    const closeMentions = useCallback(() => {
        setMentionsOpen(false);
        setMentionQuery('');
        mentionStartRef.current = null;
    }, []);
    
    const filteredMentions = React.useMemo(() => {
        const q = mentionQuery.trim().toLowerCase();
        if (!q) return mentionOptions;
        return mentionOptions.filter(x =>
            String(x.RepresentativeName || '').toLowerCase().includes(q)
        );
    }, [mentionOptions, mentionQuery]);

    useEffect(() => {
        if (!mentionsOpen) return;
        setMentionIndex(i => Math.min(i, Math.max(0, filteredMentions.length - 1)));
    }, [filteredMentions.length, mentionsOpen]);

    useEffect(() => {
        if (!mentionsOpen) return;
        const list = mentionListRef.current;
        if (!list) return;

        const item = list.children[mentionIndex];
        if (item && item.scrollIntoView) item.scrollIntoView({ block: 'nearest' });
    }, [mentionIndex, mentionsOpen, filteredMentions]);

    useEffect(() => {
        if (!mentionsOpen) return;
        const onResize = () => positionMentionPopup();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [mentionsOpen, positionMentionPopup]);
    
    const insertMention = useCallback((opt) => {
        if (!opt) return;
        const name = opt.RepresentativeName || '';
        const textarea = textareaRef.current;
        if (!textarea || mentionStartRef.current == null) return;
        
        const value = inputValue;
        const start = mentionStartRef.current;
        const caret = textarea.selectionStart;
        const before = value.slice(0, start);
        const after = value.slice(caret);
        const replacement = `@${name} `;
        
        setInputValue(before + replacement + after);
        // move caret to end of the inserted mention
        setTimeout(() => {
            const pos = (before + replacement).length;
            textarea.focus();
            textarea.setSelectionRange(pos, pos);
        }, 0);

        setSelectedMentions(prev => {
            const next = new Map(prev);
            next.set(opt.RepresentativeId, name);
            return next;
        });

        closeMentions();
    }, [inputValue, closeMentions]);

    const escapeReg = s => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const containsMentionToken = (text, label) => {
        // require @Label as a whole token; allow punctuation/whitespace after
        const re = new RegExp(`(^|[^\\w])@${escapeReg(label)}(?=$|[\\s,.!?])`, "i");
        return re.test(text || "");
    };

    // whenever inputValue changes, prune removed mentions
    React.useEffect(() => {
        setSelectedMentions(prev => {
            let changed = false;
            const next = new Map();
            for (const [id, label] of prev.entries()) {
                if (containsMentionToken(inputValue, label)) {
                    next.set(id, label);
                } else {
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [inputValue]);

    useEffect(() => { positionMentionPopup(); }, [highlightHtml, positionMentionPopup]);

    const handleKeyDown = (e) => {
        // If mention list is open, handle nav/confirm/cancel first
        if (mentionsOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                mentionNavModeRef.current = 'keyboard';
                if (mentionNavTimerRef.current) clearTimeout(mentionNavTimerRef.current);
                mentionNavTimerRef.current = setTimeout(() => { mentionNavModeRef.current = 'mouse'; }, 250);
                setMentionIndex(i =>
                    e.key === 'ArrowDown'
                        ? Math.min(i + 1, Math.max(0, filteredMentions.length - 1))
                        : Math.max(i - 1, 0)
                );
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                insertMention(filteredMentions[mentionIndex] || filteredMentions[0]);
                return;
            }
            if (e.key === 'Escape') { e.preventDefault(); closeMentions(); return; }
        }
        
        // Open mentions when typing "@"
        if (e.key === '@' && !isSendDisabled && !isTemplateLocked) {
            mentionStartRef.current = textareaRef.current?.selectionStart ?? null;
            openMentions();
            return;
        }
        
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            const scrollHeight = textarea.scrollHeight;
            const maxHeight = parseInt(textarea.style.maxHeight || '120', 10);
            textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        }
    }, [inputValue]);

    // track whether user is near the bottom
    useEffect(() => {
        const el = messagesAreaRef.current;
        if (!el) return;

        const onScroll = () => {
            const slack = 24; // px tolerance
            bottomLockRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) <= slack;
        };

        el.addEventListener('scroll', onScroll, { passive: true });
        // initialize once
        onScroll();
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        if (skipScrollRef.current) {
            skipScrollRef.current = false;
            return;
        }

        const el = messagesAreaRef.current;
        if (!el) return;

        if (isUserMessageJustSent || bottomLockRef.current) {
            el.scrollTop = el.scrollHeight;
            if (isUserMessageJustSent) setIsUserMessageJustSent(false);
        }
        // otherwise: do nothing (user is reading history)
    }, [messages, isUserMessageJustSent]);

    const templateSelectorRef = useRef(null);

    useEffect(() => {
        const handleDocMouseDown = (e) => {
            if (!isTemplatesOpen) return;
            const el = templateSelectorRef.current;
            if (el && !el.contains(e.target)) {
                setTemplateState('closing');
            }
        };

        const handleKeyDown = (e) => {
            if (!isTemplatesOpen) return;
            if (e.key === 'Escape') setTemplateState('closing');
        };

        document.addEventListener('mousedown', handleDocMouseDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleDocMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isTemplatesOpen]);

    const emojiPickerRef = useRef(null);
    const emojiButtonRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                emojiPickerRef.current &&
                !emojiPickerRef.current.contains(event.target) &&
                emojiButtonRef.current &&
                !emojiButtonRef.current.contains(event.target)
            ) {
                setShowEmojiPicker(false);
            }
        };

        if (showEmojiPicker) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        // Cleanup on unmount
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showEmojiPicker]);

    useEffect(() => {
        if (showEmojiPicker && (templateState === 'open' || templateState === 'opening')) {
            setTemplateState('closing');
        }
    }, [showEmojiPicker, templateState]);

    useEffect(() => {
        if (templateState === 'opening') {
            const t = setTimeout(() => {
                setTemplateState(s => (s === 'opening' ? 'open' : s));
            }, 260); // ~ animation duration
            return () => clearTimeout(t);
        }
        if (templateState === 'closing') {
            const t = setTimeout(() => {
                setTemplateState(s => (s === 'closing' ? 'closed' : s));
            }, 260);
            return () => clearTimeout(t);
        }
    }, [templateState]);

    return (
        <div className="chat-container">
            <div className="chat-sticky-top">
                {/* Subject header */}
                <div className="chat-header chat-header--compact">
                    {showBackButton && (
                        <div className="chat-header__backRow">
                            <button
                                className="btn btn-link p-0 d-flex align-items-center gap-2"
                                onClick={onBackClick}
                                aria-label={t("application_strings.views.chat.rightPane.chat.nav.backAria")}
                            >
                                <FiChevronLeft size={20} />
                                <span>{t("application_strings.views.chat.rightPane.chat.nav.backToList")}</span>
                            </button>
                        </div>
                    )}
                    {isEditingSubject ? (
                        <div className="chat-header__titleRow">
                            <input
                                ref={subjectInputRef}
                                value={subjectValue}
                                onChange={(e) => setSubjectValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") { e.preventDefault(); commitSubject(); }
                                    if (e.key === "Escape") { e.preventDefault(); cancelEditSubject(); }
                                }}
                                placeholder={t("application_strings.views.chat.rightPane.chat.subject.placeholder")}
                                className="form-control"
                                style={{ flex: 1 }}
                            />
                            <button className="btn btn-primary btn-sm" onClick={commitSubject}>{t("application_strings.views.chat.rightPane.chat.subject.save")}</button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={cancelEditSubject}>{t("application_strings.views.chat.rightPane.chat.subject.cancel")}</button>
                        </div>
                    ) : (
                        <div className="chat-header__titleRow">
                            <h6 className="mb-0 text-truncate chat-header__title" title={subjectValue || t("application_strings.views.chat.rightPane.chat.subject.none")}>
                                {t("application_strings.views.chat.rightPane.chat.subject.titlePrefix", { referenceNo: referenceNo})}{subjectValue || <span className="text-muted">{t("application_strings.views.chat.rightPane.chat.subject.none")}</span>}
                            </h6>
                            <div className="d-flex align-items-center gap-2">
                                <button className="btn btn-outline-secondary btn-sm" onClick={beginEditSubject} title={t("application_strings.views.chat.rightPane.chat.subject.editTitle")} disabled={readOnly}>
                                    {t("application_strings.views.chat.rightPane.chat.subject.editBtn")}
                                </button>
                                <AssigneeBadge
                                    id={`chat-assignee-${chatID}`}
                                    name={(committedAssignee?.name || representativeAssigneeName || teamAssigneeName || "").trim()}
                                    kind={committedAssignee?.kind || (representativeAssigneeName ? "User" : (teamAssigneeName ? "Team" : null))}
                                    size={28}
                                    onClick={readOnly ? undefined : () => setAssigneeOpen((o) => !o)}
                                    disabled={readOnly}
                                />
                                <AssigneePickerPopover
                                    open={assigneeOpen}
                                    anchorId={`chat-assignee-${chatID}`}
                                    chatId={chatID}
                                    onClose={() => setAssigneeOpen(false)}
                                    onAssignToMe={async () => {
                                    // optimistic
                                    setCommittedAssignee({ kind: 'User', id: null, name: currentUserName });
                                    onAssigneeUpdated?.({ kind: 'User', id: null, name: currentUserName });
                                    try {
                                        const ok = await update('Chat_SetAssignee', {
                                            ChatId: chatID, AssignToCurrent: true
                                        });
                                        if (!ok) throw new Error('server returned false');
                                    } finally {
                                        setAssigneeOpen(false);
                                    }
                                    }}
                                    onCommitted={({ kind, id, name }) => {
                                        setCommittedAssignee({ kind, id, name });
                                        onAssigneeUpdated?.({ kind, id, name }); // keeps LHS in sync
                                    }}
                                />
                                <div ref={statusAnchorRef}>
                                    <StatusPill
                                        value={statusValue}
                                        onChange={handleChangeStatus}
                                        stopClickPropagation={false}
                                        alpha={0.8}
                                        disabled={readOnly}
                                    />
                                </div>

                                {!isMobile && (
                                    <>
                                        <button
                                            className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                                            onClick={async () => {
                                                try {
                                                    const res = await OpenLinkJobsDlg({
                                                        chatID,
                                                        chatIntID: referenceNo,
                                                        chatSubject: subject || "",
                                                        contactID,
                                                        linkedJobs
                                                    });
                                                    if (res?.linkedJobs) {
                                                        // Optimistic RHS update
                                                        setLinkedJobsLocal(res.linkedJobs);
                                                        onLinkedJobsUpdated?.(res.linkedJobs);
                                                    }
                                                    // Keep RHS fresh (even if broadcast lags)
                                                    await fetchMessages();
                                                    await refreshServiceWindow();
                                                } catch (e) {
                                                    console.error("OpenLinkJobsDlg failed", e);
                                                }
                                            }}
                                            title={t("application_strings.views.chat.rightPane.chat.jobs.linkBtnTitle")}
                                            disabled={readOnly}
                                        >
                                            <FiPlus size={18} style={{ display: 'block' }} />
                                            {t("application_strings.views.chat.rightPane.chat.jobs.linkBtn")}
                                        </button>

                                        <button
                                            className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                                            onClick={() => OpenNewLeadForChat({ contactID, contactPoint, contactName })}
                                            title={t("application_strings.views.chat.rightPane.chat.jobs.newLeadBtnTitle")}
                                            disabled={readOnly}
                                        >
                                            <FiPlus size={18} style={{ display: 'block' }} />
                                            {t("application_strings.views.chat.rightPane.chat.jobs.newLeadBtn")}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        )
                    }

                    {/* Persistent toggle (stays visible even when collapsed) */}
                    <div className="chat-header__detailsToggle">
                        <button
                            type="button"
                            className="chat-button-inline chat-header__detailsBtn"
                            onClick={() => setDetailsOpen(o => !o)}
                            aria-expanded={detailsOpen}
                            aria-controls="chat-header-details"
                            title={detailsOpen ? t("application_strings.views.chat.rightPane.chat.detailsToggle.hide") : t("application_strings.views.chat.rightPane.chat.detailsToggle.show")}
                        >
                            {detailsOpen
                                ? <FiChevronUp
                                    className={`chat-header__detailsChevron ${detailsOpen ? 'open' : ''}`}
                                    aria-hidden="true"
                                />
                                : <FiChevronDown
                                    className={`chat-header__detailsChevron ${detailsOpen ? 'open' : ''}`}
                                    aria-hidden="true"
                                />
                            }
                            <span className="chat-header__detailsLabel chat-header__detailsLabel--full">
                                {detailsOpen ? t("application_strings.views.chat.rightPane.chat.detailsToggle.hide") : t("application_strings.views.chat.rightPane.chat.detailsToggle.show")}
                            </span>
                            <span className="chat-header__detailsLabel chat-header__detailsLabel--short">
                                {detailsOpen ? t("application_strings.views.chat.rightPane.chat.detailsToggle.hideShort") : t("application_strings.views.chat.rightPane.chat.detailsToggle.showShort")}
                            </span>
                        </button>
                    </div>

                    <div
                      id="chat-header-details"
                      className={`chat-header__collapsible ${detailsOpen ? 'open' : 'closed'}`}
                    >
                      {/* Collapsible: contact line + meta */}
                        <div className="chat-header__contactLine">
                            {contactPoint && (
                                <a href={`tel:${contactPoint}`} className="chat-header__telLink" title={t("application_strings.views.chat.rightPane.chat.contactLine.callTitle")} aria-label={t("application_strings.views.chat.rightPane.chat.contactLine.callAria")}>
                                    <AccentIcons.Phone noPadding className="chat-header__telIcon" />
                                </a>
                            )}
                            <small className="text-truncate">
                                {contactID ? (
                                    <>
                                        <A className="acc-link" href={`/Contact/${contactID}`} title={t("application_strings.views.chat.rightPane.chat.contactLine.openContactTitle")}>
                                            {contactName || t("application_strings.views.chat.rightPane.chat.contactLine.fallbackContact") }
                                        </A>
                                        {` (${contactPointDisplayName})`}
                                        {fromDisplayName && <span className="text-muted"> · {t("application_strings.views.chat.rightPane.chat.contactLine.via", { fromDisplayName: fromDisplayName })}</span>}
                                    </>
                                ) : (
                                    <>
                                        {contactPoint ? `${contactPointDisplayName}` : t("application_strings.views.chat.rightPane.chat.contactLine.unlinked")}
                                        {fromDisplayName && <span className="text-muted"> · {t("application_strings.views.chat.rightPane.chat.contactLine.via", { fromDisplayName: fromDisplayName })}</span>}
                                    </>
                                )}
                            </small>
                        </div>

                        {Array.isArray(linkedJobsLocal) && linkedJobsLocal.length > 0 && (
                            <div className="chat-header__links">
                                <small className="text-muted me-2">{t("application_strings.views.chat.rightPane.chat.jobs.linkedLabel", { plural: linkedJobsLocal.length > 1 ? 's' : '' })}</small>
                                {linkedJobsLocal.map((j, i) => (
                                    <React.Fragment key={j.JobID}>
                                        <A className="acc-link" href={`/Job/${j.JobID}`} title={t("application_strings.views.chat.rightPane.chat.jobs.openJobTitle", { jobRef: j.Reference || j.JobID })}>
                                            {j.Reference || t("application_strings.views.chat.rightPane.chat.jobs.jobNumber", { jobId: j.JobID })}
                                        </A>
                                        {i < linkedJobsLocal.length - 1 && <span className="text-muted mx-1">|</span>}
                                    </React.Fragment>
                                ))}
                            </div>
                        )}

                        <div className="chat-header__meta">
                            <small>
                                {t("application_strings.views.chat.rightPane.chat.meta.openedAt", { date: openDate ? format.formatDateTime(openDate) : "—" })}
                            </small>

                            {closeDateValue && (
                                <>
                                    <span aria-hidden="true">·</span>
                                    <small>
                                        {t("application_strings.views.chat.rightPane.chat.meta.closedAt", { date: format.formatDateTime(closeDateValue) })}
                                    </small>
                                </>
                            )}

                            {archivedDate && archivedBy && (
                                <small className="chat-header__metaRight">
                                    {t("application_strings.views.chat.rightPane.chat.meta.archivedAtBy", { date: format.formatDateTime(archivedDate), name: archivedBy })}
                                </small>
                            )}
                        </div>
                    </div>
                </div>

                <ServiceWindowBanner
                    isOpen={isWindowOpen}
                    secondsRemaining={serviceWindow?.SecondsRemaining ?? 0}
                    expiresAtUtc={serviceWindow?.ExpiresAtUtc}
                />

                {/* Suggested contact hover-card (supports multiple matches) */}
                {suggestedMatches.length > 0 && (
                    <div className="chat-match-suggestion" title={t("application_strings.views.chat.rightPane.chat.suggestions.foundTitle")}>
                        <div
                            className={`chat-match-suggestion-card ${chipsPhase === 'enter'
                                    ? 'chat-template-list-enter'
                                    : chipsPhase === 'exit'
                                        ? 'chat-template-list-exit'
                                        : ''
                                }`}
                            onAnimationEnd={(e) => {
                                if (e.target !== e.currentTarget) return;
                                if (chipsPhase === 'enter') setChipsPhase('idle');
                            }}
                        >
                            <span className="text-muted">{t("application_strings.views.chat.rightPane.chat.suggestions.possiblePrefix", { plural: suggestedMatches.length > 1 ? 's' : '' })}</span>
                            <div className="chat-chip-list">
                                {suggestedMatches.map(m => {
                                    const id = m.ContactID ?? m.ID ?? `${m.FirstName}|${m.LastName}`;
                                    const name = [m.FirstName, m.LastName].filter(Boolean).join(" ") || "Unknown";
                                    const keyFor = (x) => x.ContactID ?? x.ID ?? `${x.FirstName}|${x.LastName}`;
                                    return (
                                        <div className="chat-chip" key={id}>
                                            <span className="chat-chip-name">{name}</span>
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={() => handleLinkContact(m)}
                                            >
                                                {t("application_strings.views.chat.rightPane.chat.suggestions.link")}
                                            </button>

                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() =>
                                                    setSuggestedMatches(prev => {
                                                        const next = prev.filter(x => keyFor(x) !== id);
                                                        if (next.length === 0) {
                                                            hideChipsWithAnimation();
                                                            return prev;
                                                        }
                                                        return next;
                                                    })
                                                }
                                            >
                                                {t("application_strings.views.chat.rightPane.chat.suggestions.dismiss")}
                                            </button>
                                        </div>
                                    );
                                })}
                                <button className="btn btn-primary btn-sm" onClick={() => OpenNewContactForChat({chatID, contactPoint})}>
                                    {t("application_strings.views.chat.rightPane.chat.suggestions.create")}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {!contactID && contactPoint && suggestionsReady && suggestedMatches.length === 0 && (
                    <div className="chat-match-suggestion">
                        <div className="chat-match-suggestion-card">
                            <span className="text-muted">{t("application_strings.views.chat.rightPane.chat.suggestions.noneFound", { contactPoint: contactPoint })}</span>
                            <button className="btn btn-primary btn-sm" onClick={() => OpenNewContactForChat({ chatID, contactPoint })}>
                                {t("application_strings.views.chat.rightPane.chat.suggestions.create")}
                            </button>
                        </div>
                    </div>
                )}

            </div>

            {loading && isInitialLoad && <div className="chat-loading-indicator">{t("application_strings.views.chat.rightPane.chat.list.loadingInitial")}</div>}

            <div className="chat-messages-area" ref={messagesAreaRef}>
                {totalMessages > messages.length && !loading && (
                    <button
                        className="chat-load-more"
                        onClick={() => {
                            if (!isLoadingOlder) {
                                setDataState(prev => ({ ...prev, skip: prev.skip + 50 }));
                            }
                        }}
                        disabled={isLoadingOlder || loading}
                    >
                        {isLoadingOlder ? t("application_strings.views.chat.rightPane.chat.list.loadingOlder") : t("application_strings.views.chat.rightPane.chat.list.loadOlder")}
                    </button>
                )}
                {messages.map((message, index) => {
                    const isUnread = unreadMessageIds.has(String(message.id));
                    const showDivider = index === firstUnreadIndex;

                    return (
                        <React.Fragment key={message.id}>
                            {showDivider && (
                                <div className="chat-unread-divider">
                                    {t("application_strings.views.chat.rightPane.chat.list.unreadDivider")}
                                </div>
                            )}
                            <ChatMessage
                                key={message.id}
                                {...message}
                                isUnread={isUnread}
                                onResend={handleResendMessage}
                                alternateChannels={alternateChannelSaveMessage.map(x => x.channel)}
                                onAlternateChannelSend={handleAlternateChannelSend}
                                onStartReply={startReply}
                                onJumpToMessage={jumpToMessage}
                                mentionNameSet={mentionNameSet}
                                meNameLower={currentUserName?.toLowerCase()}
                                contactID={contactID}
                                contactName={contactName}
                            />
                        </React.Fragment>
                    );
                })}
                <div ref={messageEndRef} />
            </div>

            <div className={`chat-input-area ${isInternal ? 'internal' : ''}`} ref={inputAreaRef}>
                {templates.length > 0 && (
                    <div ref={templateSelectorRef} className="chat-template-selector">
                         {templateState !== 'closed' && (
                            <div
                                className={
                                    `chat-template-list ${
                                        templateState === 'opening'
                                            ? 'chat-template-list-enter'
                                            : templateState === 'closing'
                                            ? 'chat-template-list-exit'
                                            : ''
                                    }`
                                }
                                aria-hidden={!isTemplatesOpen}
                                onAnimationEnd={(e) => {
                                    if (e.target !== e.currentTarget) return;
                                    setTemplateState(s => (s === 'opening' ? 'open' : s === 'closing' ? 'closed' : s));
                                }}
                            >
                                {templatePhase === 'job' ? (
                                    <div className="chat-template-job-list">
                                        {(linkedJobsLocal || []).map(j => (
                                            <button
                                                key={j.JobID}
                                                className="chat-template-item"
                                                onClick={() => {
                                                  setSelectedJobIdForTemplate(j.JobID);
                                                  setTemplatePhase('templates');
                                                }}
                                            >
                                                {j.Reference || t("application_strings.views.chat.rightPane.chat.jobs.jobNumber", { jobId: j.JobID })}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    templates.map((template, index) => (
                                        <button
                                            key={index}
                                            className="chat-template-item"
                                            onClick={() => handleTemplateClick(template)}
                                        >
                                            {template.Description}
                                        </button>
                                    ))
                                )}
                            </div>
                        )}

                        <button
                            className={`chat-floating-template-button ${showEmojiPicker
                                ? 'chat-floating-template-button-exit'
                                : 'chat-floating-template-button-enter'}`}
                            onClick={() => {
                                const linked = Array.isArray(linkedJobsLocal) ? linkedJobsLocal : [];
                                const toggle = (s) => (s === 'open' || s === 'opening') ? 'closing' : 'opening';

                                if (!hasCtxJob && linked.length > 1) {
                                    // Step A: pick job, but still toggle if already open
                                    setTemplatePhase('job');
                                    setSelectedJobIdForTemplate(null);
                                    setTemplateState(toggle);
                                    return;
                                }

                                if (!hasCtxJob && linked.length === 1) {
                                    setSelectedJobIdForTemplate(linked[0]?.JobID ?? null);
                                }

                                // Step B: show templates; still a proper toggle
                                setTemplatePhase('templates');
                                setTemplateState(toggle);
                            }}
                            aria-hidden={showEmojiPicker}
                            tabIndex={showEmojiPicker ? -1 : 0}
                        >
                            <FiPlus size={20} />
                            <span>{t("application_strings.views.chat.rightPane.chat.compose.insertTemplate")}</span>
                        </button>
                    </div>
                )}

                <input
                    type="file"
                    accept="*/*"
                    multiple
                    ref={fileInputRef}
                    onChange={(e) => {
                        if (e.target.files?.length) addFiles(e.target.files);
                        e.target.value = null;
                    }}
                    style={{ display: 'none' }}
                />
                {replyingTo && (
                    <div className="chat-reply-preview">
                        {replyingTo.imageUrl ? <img className="chat-reply-thumb" src={replyingTo.imageUrl} alt="thumb" /> : null}
                        <div className="chat-reply-preview-content">
                            <div className="chat-reply-preview-title">{t("application_strings.views.chat.rightPane.chat.compose.replyingTo", { name: replyingTo.senderName})}</div>
                            <div className="chat-reply-preview-text">{replyingTo.textPreview}</div>
                        </div>
                        <button className="chat-reply-preview-close" onClick={cancelReply} aria-label={t("application_strings.views.chat.rightPane.chat.compose.cancelReplyAria")}>×</button>
                    </div>
                )}

                {pendingAttachments.length > 0 && (
                    <div className="chat-attachment-tray">
                        {pendingAttachments.map(att => (
                            <div key={att.id} className="chat-attachment-chip" title={`${att.name} (${Math.ceil(att.size / 1024)} KB)`}>
                                {att.type?.startsWith('image/') && att.previewUrl ? (
                                    <img src={att.previewUrl} alt={att.name} className="chat-attachment-thumb" />
                                    ) : att.type?.startsWith('video/') ? (
                                        <FiVideo className="chat-attachment-icon" />
                                    ) : att.type?.startsWith('audio/') ? (
                                        <FiMusic className="chat-attachment-icon" />
                                    ) : (
                                        <FiPaperclip className="chat-attachment-icon" />
                                    )}
                                <span className="chat-attachment-name">{att.name}</span>
                                <button className="chat-attachment-remove" onClick={() => removeAttachment(att.id)}>×</button>
                            </div>
                        ))}
                        <button className="chat-attachment-clear" onClick={clearAttachments}>{t("application_strings.views.chat.rightPane.chat.attachments.clear")}</button>
                    </div>
                )}

                <div className="chat-input-container">
                    <div className="d-flex align-items-center">
                        <div className="form-check form-switch m-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="toggle-internal"
                                checked={isInternal}
                                onChange={(e) => setIsInternal(e.target.checked)}
                                disabled={readOnly}
                            />
                            <label className="form-check-label" htmlFor="toggle-internal" title={isInternal ? t("application_strings.views.chat.rightPane.chat.compose.internalHint") : t("application_strings.views.chat.rightPane.chat.compose.externalHint")}>
                                {isInternal ? t("application_strings.views.chat.rightPane.chat.compose.internal") : t("application_strings.views.chat.rightPane.chat.compose.external")}
                            </label>
                        </div>
                    </div>
                    <span className={`chat-input-divider ${isInternal ? 'internal' : ''}`} aria-hidden="true" />
                    <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
                        <div
                            ref={mentionWrapRef}
                            className={`chat-input-wrapper ${isSendDisabled ? 'chat-input-wrapper-disabled' : ''}`}
                            data-tooltip={t("application_strings.views.chat.rightPane.chat.compose.disabledTooltip")}
                        >
                            {showHtmlPreview && (
                                <div
                                    className="chat-input-preview"
                                    disabled={true}
                                    role="region"
                                >
                                {isTemplateLocked && (
                                    <button
                                        type="button"
                                        className="chat-preview-clear"
                                        aria-label={t("application_strings.views.chat.rightPane.chat.compose.clearTemplateAria")}
                                        title={t("application_strings.views.chat.rightPane.chat.compose.clearTemplateAria")}
                                        onClick={() => {
                                            setSelectedTemplateID(0);
                                            setTemplateMode('none');
                                            setInputValue('');
                                        }}
                                    >
                                        ×
                                    </button>
                                    )}
                                    {renderSafeHtml(inputValue)}
                                </div>
                            )}

                            <div
                                className="chat-input-highlights"
                                aria-hidden="true"
                                ref={highlightsRef}
                                style={{ display: showHtmlPreview ? 'none' : undefined }}
                                dangerouslySetInnerHTML={{ __html: highlightHtml }}
                            />

                            <textarea
                                ref={textareaRef}
                                value={inputValue}
                                onChange={(e) => {
                                    setInputValue(e.target.value);
                                    if (templateMode === 'detached') setSelectedTemplateID(0);
                                    // If mention picker is open, update the live query and close when the token ends
                                    if (mentionsOpen) {
                                        const caret = e.target.selectionStart ?? e.target.value.length;
                                        const at = mentionStartRef.current;
                                        // basic guard rail
                                        if (at == null || at >= e.target.value.length) { closeMentions(); return; }
                                        const slice = e.target.value.slice(at, caret); // includes "@"
                                        // still inside the "@word" token?
                                        const m = /^@([^\s.,!?]*)$/.exec(slice);
                                        if (m) {
                                            setMentionQuery(m[1]);
                                        } else {
                                            closeMentions();
                                        }
                                    }
                                }}
                                onKeyDown={handleKeyDown}
                                placeholder={isMobile ? t("application_strings.views.chat.rightPane.chat.compose.placeholderMobile") : t("application_strings.views.chat.rightPane.chat.compose.placeholderDesktop")}
                                className="chat-input"
                                rows={1}
                                disabled={isSendDisabled || isTemplateLocked}
                                style={{
                                    opacity: isSendDisabled ? 0.5 : 1,
                                    width: '100%',
                                    visibility: showHtmlPreview ? 'hidden' : 'visible'
                                }}
                            />

                            {mentionsOpen && filteredMentions.length > 0 && (
                                <div
                                    ref={mentionListRef}
                                    className="chat-mentions-popup"
                                    style={{ left: mentionPopupLeft }}
                                    role="listbox"
                                >
                                    {filteredMentions.map((opt, i) => (
                                        <div
                                            key={String(opt.RepresentativeId)}
                                            onMouseDown={(ev) => { ev.preventDefault(); insertMention(opt); }}
                                            onMouseEnter={() => { if (mentionNavModeRef.current === 'mouse') setMentionIndex(i); }}
                                            className={i === mentionIndex ? 'active' : ''}
                                            style={{
                                                padding: '8px 12px', cursor: 'pointer',
                                                background: i === mentionIndex ? 'rgba(13,110,253,.08)' : 'transparent'
                                            }}
                                            role="option"
                                            aria-selected={i === mentionIndex}
                                        >
                                            {opt.RepresentativeName}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {isGeneratingTemplate && (
                                <div className="chat-compose-overlay">
                                    <AccentSpinner />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="emoji-anchor">
                        {selectedTemplateID !== 0 && (
                            <button
                                type="button"
                                className="chat-icon-button"
                                onClick={() => setTemplateMode(m => m === 'locked' ? 'detached' : 'locked')}
                                title={isTemplateLocked ? t("application_strings.views.chat.rightPane.chat.compose.unlockTitle") : t("application_strings.views.chat.rightPane.chat.compose.lockTitle")}
                            >
                                {isTemplateLocked ? <FiUnlock size={20} /> : <FiLock size={20} />}
                            </button>
                        )}

                        {!isMobile && (
                            <button
                                ref={emojiButtonRef}
                                className={`chat-icon-button ${!isSendDisabled ? (showEmojiPicker ? 'chat-icon-button-active' : '') : 'chat-icon-button-inactive'}`}
                                onClick={toggleEmojiPicker}
                                disabled={isSendDisabled}
                            >
                                <FiSmile size={20} />
                            </button>
                        )}

                        <button
                            className="chat-icon-button"
                            onClick={() => fileInputRef.current?.click()}
                            title={t("application_strings.views.chat.rightPane.chat.compose.attachFileTitle")}
                            disabled={isSendDisabled}
                            style={{
                                opacity: isSendDisabled ? 0.5 : 1,
                                cursor: isSendDisabled ? "not-allowed" : "pointer"
                            }}
                        >
                            <FiPaperclip size={20} />
                        </button>

                        {!isMobile && showEmojiPicker && (
                            <div className="chat-emoji-picker" ref={emojiPickerRef}>
                                {['😀', '😂', '😍', '👍', '🤔', '❤️'].map((emoji) => (
                                    <button key={emoji} onClick={() => { handleEmojiClick(emoji); setSelectedTemplateID(0); }} className="chat-emoji-button">
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={handleSendMessage}
                        disabled={!canSendNow}
                        className={`chat-send-button ${canSendNow
                          ? 'chat-send-button-active'
                          : 'chat-send-button-inactive'
                        }`}
                    >
                        <FiSend size={16} />
                    </button>
                </div>
            </div>
            <AccentPopup
                show={uniqPopup}
                anchor={statusAnchorRef}
                onAutoClose={() => setUniqPopup(false)}
                anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
                popupAlign={{ horizontal: 'center', vertical: 'top' }}
                headerContent={<strong>{t("application_strings.views.chat.rightPane.chat.uniqueness.title")}</strong>}
                actionsContent={
                    <button className="btn btn-primary btn-sm ms-auto" onClick={() => setUniqPopup(false)}>
                        {t("application_strings.views.chat.rightPane.chat.uniqueness.ok")}
                    </button>
                }
            >
                {t("application_strings.views.chat.rightPane.chat.uniqueness.message")}
            </AccentPopup>
        </div>
    );
});

export default ChatCtrl;
